/**
 * Hook to fetch and manage system users from Supabase
 * Used for role assignments, participant selection, etc.
 *
 * MULTI-TENANT: Only returns users within the current user's admin scope:
 * - Platform Admin: All users
 * - Tenant Admin: Users in their tenant
 * - Workspace Admin: Users in their workspace
 * - Others: Only users in the current tenant (if selected)
 *
 * PARTICIPANT SAFETY: Workspace-scoped fetches always go through the
 * get_workspace_participants RPC (SECURITY DEFINER), which excludes
 * platform/system super-admins from the returned list. This prevents
 * platform admins who hold a workspace_memberships row (e.g. for support
 * access) from appearing in participant selection dropdowns.
 */

import { useState, useEffect } from 'react';
import { getSafeSupabaseClient } from '../lib/supabaseSync';
import { resolveAdminScope, type AdminScope } from '../lib/adminScope';
import { useUserStore } from '../stores/prioritization/userStore';
import type { ActorRole } from '../types/prioritization/roles';

export interface SystemUser {
  id: string;
  externalId: string;
  email: string;
  name: string;
  avatarUrl?: string;
  metadata?: {
    title?: string;
    department?: string;
    location?: string;
  };
  createdAt: Date;
}

interface UseSystemUsersResult {
  users: SystemUser[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseSystemUsersOptions {
  /** If provided, only return users from this specific workspace */
  workspaceId?: string | null;
  /** If provided, only return users from this specific tenant */
  tenantId?: string | null;
}

// Get current tenant ID from session storage
function getCurrentTenantId(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem('peakPriorities_currentTenantId');
}

// Get current workspace ID from session storage
function getCurrentWorkspaceId(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem('peakPriorities_currentWorkspaceId');
}

export function useSystemUsers(options?: UseSystemUsersOptions): UseSystemUsersResult {
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get current user's role to determine scope
  const currentRole = useUserStore(state => state.currentRole);
  const assignedRoleNames = useUserStore(state => state.assignedRoleNames);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    const client = getSafeSupabaseClient();
    if (!client) {
      setLoading(false);
      return;
    }

    try {
      // Check if explicit options were provided (not just falsy but actually passed)
      const hasExplicitWorkspace = options?.workspaceId !== undefined && options?.workspaceId !== null;
      const hasExplicitTenant = options?.tenantId !== undefined && options?.tenantId !== null;
      
      // Use explicit options if provided, otherwise fall back to session storage
      const tenantId = options?.tenantId ?? getCurrentTenantId();
      const workspaceId = options?.workspaceId ?? getCurrentWorkspaceId();
      const { currentRole: roleForScope, assignedRoleNames: assignmentRoles } = useUserStore.getState();
      const adminScope = resolveAdminScope(roleForScope, assignmentRoles);
      const isPlatformScope = adminScope === 'platform';
      
      // If explicit workspace/tenant provided via options, use that scope regardless of admin level
      const useExplicitScope = hasExplicitWorkspace || hasExplicitTenant;
      
      console.log('[useSystemUsers] Fetching with scope:', {
        optionsWorkspaceId: options?.workspaceId,
        optionsTenantId: options?.tenantId,
        hasExplicitWorkspace,
        hasExplicitTenant,
        resolvedWorkspaceId: workspaceId,
        resolvedTenantId: tenantId,
        adminScope,
        useExplicitScope,
        isPlatformAdmin: isPlatformScope,
      });
      
      let data;
      
      // If explicit workspace scope is provided, fetch users from that workspace
      if (hasExplicitWorkspace && workspaceId) {
        // Use the RPC which excludes platform/system super-admins (SECURITY DEFINER).
        const { data: rpcData, error: rpcError } = await client
          .rpc('get_workspace_participants', { p_workspace_id: workspaceId });
        
        if (rpcError) {
          console.warn('[useSystemUsers] get_workspace_participants RPC failed, falling back to direct query:', rpcError);
          // Fallback: direct workspace_memberships query (no super-admin filter)
          const { data: memberships } = await client
            .from('workspace_memberships')
            .select('user_id')
            .eq('workspace_id', workspaceId)
            .eq('status', 'active');
          const userIds = (memberships ?? []).map(m => m.user_id);
          if (userIds.length > 0) {
            const { data: userData, error: fetchError } = await client
              .from('users').select('*').in('id', userIds).order('name', { ascending: true });
            if (fetchError) throw fetchError;
            data = userData;
          } else {
            // No workspace memberships - try tenant fallback
            console.warn('[useSystemUsers] No workspace memberships found, trying tenant fallback for workspace:', workspaceId);
            const { data: workspaceData } = await client
              .from('workspaces').select('tenant_id').eq('id', workspaceId).single();
            if (workspaceData?.tenant_id) {
              const { data: tenantMemberships } = await client
                .from('tenant_memberships').select('user_id').eq('tenant_id', workspaceData.tenant_id);
              const tIds = (tenantMemberships ?? []).map(m => m.user_id);
              if (tIds.length > 0) {
                const { data: userData, error: fetchError } = await client
                  .from('users').select('*').in('id', tIds).order('name', { ascending: true });
                if (fetchError) throw fetchError;
                data = userData;
              } else {
                data = [];
              }
            } else {
              data = [];
            }
          }
        } else {
          console.log('[useSystemUsers] RPC returned', (rpcData ?? []).length, 'workspace participants (super-admins excluded)');
          data = rpcData;
        }
      }
      // If explicit tenant scope is provided, fetch users from that tenant
      else if (hasExplicitTenant && tenantId) {
        const { data: memberships, error: membershipError } = await client
          .from('tenant_memberships')
          .select('user_id')
          .eq('tenant_id', tenantId);
        
        if (membershipError) {
          console.warn('[useSystemUsers] Failed to fetch tenant memberships for explicit scope:', membershipError);
          data = [];
        } else if (memberships && memberships.length > 0) {
          const userIds = memberships.map(m => m.user_id);
          
          const { data: userData, error: fetchError } = await client
            .from('users')
            .select('*')
            .in('id', userIds)
            .order('name', { ascending: true });
          
          if (fetchError) throw fetchError;
          data = userData;
        } else {
          data = [];
        }
      }
      // Platform admins: prefer tenant/workspace scope if available, otherwise fetch all
      else if (isPlatformScope) {
        // If we have a workspace context, use it even for platform admins.
        // Still use the RPC so the result set is consistent with what
        // non-admin users see (super-admins excluded).
        if (workspaceId) {
          console.log('[useSystemUsers] Platform admin using workspace scope:', workspaceId);
          const { data: rpcData, error: rpcError } = await client
            .rpc('get_workspace_participants', { p_workspace_id: workspaceId });
          if (rpcError) {
            console.warn('[useSystemUsers] get_workspace_participants RPC failed for platform admin:', rpcError);
            data = [];
          } else {
            data = rpcData;
          }
        }
        // If we have a tenant context, use it
        else if (tenantId) {
          console.log('[useSystemUsers] Platform admin using tenant scope:', tenantId);
          const { data: memberships, error: membershipError } = await client
            .from('tenant_memberships')
            .select('user_id')
            .eq('tenant_id', tenantId);
          
          if (membershipError) {
            console.warn('[useSystemUsers] Failed to fetch tenant memberships:', membershipError);
            data = [];
          } else if (memberships && memberships.length > 0) {
            const userIds = memberships.map(m => m.user_id);
            const { data: userData, error: fetchError } = await client
              .from('users')
              .select('*')
              .in('id', userIds)
              .order('name', { ascending: true });
            if (fetchError) throw fetchError;
            data = userData;
          } else {
            data = [];
          }
        }
        // No context at all - fetch all users (last resort)
        else {
          console.log('[useSystemUsers] Platform admin fetching all users (no context)');
          const { data: allUsers, error: fetchError } = await client
            .from('users')
            .select('*')
            .order('name', { ascending: true });
          
          if (fetchError) throw fetchError;
          data = allUsers;
        }
      } 
      // Tenant admins see users in their tenant
      else if (adminScope === 'tenant' && tenantId) {
        const { data: memberships, error: membershipError } = await client
          .from('tenant_memberships')
          .select('user_id')
          .eq('tenant_id', tenantId);
        
        if (membershipError) {
          console.warn('[useSystemUsers] Failed to fetch tenant memberships:', membershipError);
          data = [];
        } else if (memberships && memberships.length > 0) {
          const userIds = memberships.map(m => m.user_id);
          
          const { data: userData, error: fetchError } = await client
            .from('users')
            .select('*')
            .in('id', userIds)
            .order('name', { ascending: true });
          
          if (fetchError) throw fetchError;
          data = userData;
        } else {
          data = [];
        }
      }
      // Workspace admins see users in their workspace (super-admins excluded via RPC)
      else if (adminScope === 'workspace' && workspaceId) {
        const { data: rpcData, error: rpcError } = await client
          .rpc('get_workspace_participants', { p_workspace_id: workspaceId });
        if (rpcError) {
          console.warn('[useSystemUsers] get_workspace_participants RPC failed for workspace admin:', rpcError);
          data = [];
        } else {
          data = rpcData;
        }
      }
      // Regular users with a tenant selected see tenant users
      else if (tenantId) {
        const { data: memberships, error: membershipError } = await client
          .from('tenant_memberships')
          .select('user_id')
          .eq('tenant_id', tenantId);
        
        if (membershipError) {
          console.warn('[useSystemUsers] Failed to fetch tenant memberships:', membershipError);
          data = [];
        } else if (memberships && memberships.length > 0) {
          const userIds = memberships.map(m => m.user_id);
          
          const { data: userData, error: fetchError } = await client
            .from('users')
            .select('*')
            .in('id', userIds)
            .order('name', { ascending: true });
          
          if (fetchError) throw fetchError;
          data = userData;
        } else {
          console.log('[useSystemUsers] No tenant memberships found for tenant:', tenantId);
          data = [];
        }
      } else {
        // No tenant/workspace context and not a platform admin - return empty
        console.log('[useSystemUsers] No scope context, returning empty user list');
        data = [];
      }

      // Transform from snake_case to camelCase
      const transformed: SystemUser[] = (data || []).map((u: Record<string, unknown>) => ({
        id: u.id as string,
        externalId: u.external_id as string,
        email: u.email as string,
        name: u.name as string || u.email as string,
        avatarUrl: u.avatar_url as string | undefined,
        metadata: u.metadata as SystemUser['metadata'],
        createdAt: new Date(u.created_at as string),
      }));

      // Deduplicate by user ID to prevent duplicate entries in dropdowns
      const seenIds = new Set<string>();
      const uniqueUsers: SystemUser[] = [];
      for (const user of transformed) {
        if (!seenIds.has(user.id)) {
          seenIds.add(user.id);
          uniqueUsers.push(user);
        } else {
          console.warn('[useSystemUsers] Duplicate user ID detected:', user.id, user.name);
        }
      }
      
      // Debug log to catch duplicates
      if (transformed.length !== uniqueUsers.length) {
        console.warn('[useSystemUsers] Removed', transformed.length - uniqueUsers.length, 'duplicate users from', transformed.length, 'total');
      }
      
      console.log('[useSystemUsers] Fetched', uniqueUsers.length, 'unique users');

      setUsers(uniqueUsers);
    } catch (err) {
      console.error('[useSystemUsers] Failed to fetch users:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentRole, assignedRoleNames, options?.workspaceId, options?.tenantId]); // Re-fetch when role or scope changes

  return { users, loading, error, refetch: fetchUsers };
}

/**
 * Get a user's display info (name + title if available)
 */
export function getUserDisplayInfo(user: SystemUser): { name: string; subtitle?: string } {
  return {
    name: user.name,
    subtitle: user.metadata?.title || user.metadata?.department,
  };
}
